import { Permission } from '@shared/infrastructure/auth/permission.js';
import type { AppModule } from '@shared/presentation/router/composition.js';
import { CONTROL_DETAIL_ROUTE } from '../lib/control-detail.js';
import { CONTROL_EXECUTION_DETAIL_ROUTE, EXECUTION_DETAIL_ROUTE } from '../lib/execution-detail.js';
import { VP_SECTION_NAV } from '../presentation/section-nav.js';

/**
 * `analysis-groups` feature of Virtual Profiler.
 *
 * HU-25 a HU-32. Aquí vive la regla de borrado propia y el diff entre versiones,
 * que es la parte más diferencial del módulo.
 *
 * It renders inside the shell, hence `layoutRoutes`. With no `meta.public` the
 * guard protects it, which is the default.
 */
export const virtualProfilerAnalysisGroupsModule: AppModule = {
  name: 'virtual-profiler-analysis-groups',
  routes: [],
  layoutRoutes: [
    {
      path: 'virtual-profiler/control',
      name: 'virtual-profiler-analysis-groups',
      component: () => import('./presentation/ControlBoardView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_READ,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /** Where the list lived until 2026-10-14: old links and bookmarks still land on it. */
    {
      path: 'virtual-profiler/groups',
      redirect: { name: 'virtual-profiler-analysis-groups' },
    },
    /**
     * The detail of one group.
     *
     * Its own route rather than a panel, because it is what an experiment is read from and it
     * has to be linkable: «mira el grupo 3» is a thing people say, and a modal has no address.
     */
    {
      path: 'virtual-profiler/groups/:groupId',
      name: 'virtual-profiler-group-detail',
      component: () => import('./presentation/AnalysisGroupDetailView.vue'),
      // A detail: it slides in over the list it was opened from.
      meta: {
        permission: Permission.VIRTUAL_PROFILER_READ,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /**
     * One control (product owner, 2026-10-06): its figures against their targets, its executions
     * and, for the two that measure gestures, which video missed which gesture.
     *
     * By kind, because a control is its kind: it has no id (since 2026-10-28 it is not a group at
     * all), and «el control de gestos» has to keep its link.
     */
    {
      path: 'virtual-profiler/control/:kind',
      name: CONTROL_DETAIL_ROUTE,
      component: () => import('./presentation/ControlGroupDetailView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_READ,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /**
     * One execution, video by video (product owner, 2026-10-12): opened from a row of the
     * executions table of a group — so under the group, whose label names it.
     */
    {
      path: 'virtual-profiler/groups/:groupId/executions/:batch',
      name: EXECUTION_DETAIL_ROUTE,
      component: () => import('./presentation/ExecutionDetailView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_READ,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
    },
    /** The same page for a control's execution, under the control by its kind (2026-10-28). */
    {
      path: 'virtual-profiler/control/:kind/executions/:batch',
      name: CONTROL_EXECUTION_DETAIL_ROUTE,
      component: () => import('./presentation/ExecutionDetailView.vue'),
      meta: {
        permission: Permission.VIRTUAL_PROFILER_READ,
        slidesIn: true,
        sectionNav: VP_SECTION_NAV,
      },
    },
  ],
};
